import React, { useState } from 'react';

import ExpandLessTwoToneIcon from '@material-ui/icons/ExpandLessTwoTone';
import ExpandMoreTwoToneIcon from '@material-ui/icons/ExpandMoreTwoTone';
import { ReactComponent as CalendarIcon } from 'assets/pics/Calendar.svg';
import { ReactComponent as ClockIcon } from 'assets/pics/clock.svg';
import { ReactComponent as DownloadIcon } from 'assets/pics/downloadIcon.svg';
import { ReactComponent as EuroIcon } from 'assets/pics/euroIcon.svg';
import LocationIcon from 'assets/pics/locationIcon.svg';
import { ReactComponent as ParticipantsIcon } from 'assets/pics/participantsIcon.svg';
import TickIcon from 'assets/pics/tick.svg';
import { isToday } from 'date-fns';
import PropTypes from 'prop-types';
import Truncate from 'react-truncate';

import { getEventDaysForDisplay } from 'components/Event/EventCardMetaData/formatEventCardMetaData';
import { isNewEntry } from 'components/OverviewLists/utils/isNewEntry';
import { Tags } from 'components/Tags/Tags';

import { FORM_CONSTANTS } from 'global/constants';

import { formatDate } from 'utils/DatesUtils';
import { getIsRegistrationExpired } from 'utils/EventDateUtils';
import { getSanitzedAttachmentUrl } from 'utils/UrlUtil';

import './EventMetaData.sass';

export const EventMetaData = ({ event, isOverview = false, iamType }) => {
  const {
    title,
    maxParticipants,
    registration,
    description,
    location,
    eventDays,
    tags,
    attachmentUrl,
    registrationDeadline,
    meetingLink,
    disableMaxParticipation = false,
    chargingAmount,
  } = event;

  const { eventParticipantsNumber, participants } = registration;
  const isEiamUser = iamType === 'EIAM';
  const isRegistered = participants.length > 0;

  const isRegistrationExpired = getIsRegistrationExpired(event);
  const eventDaysForDisplay = getEventDaysForDisplay(eventDays);

  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleTruncate = (truncatedNow) => {
    if (truncated !== truncatedNow) {
      setTruncated(truncatedNow);
    }
  };

  const toggleLines = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
  };
  const getOnlineEventLink = () => {
    if (!isRegistered) {
      return <div>Link nach Anmeldung</div>;
    }
    if (isToday(new Date(event.startDate))) {
      return (
        <a
          href={meetingLink}
          target="_blank"
          rel="noreferrer"
          className="event-meta-data__item-link">
          Jetzt teilnehmen
        </a>
      );
    }
    return <div>Link wird am Veranstaltungstag hier freigeschaltet.</div>;
  };

  const renderLocation = () => {
    if (meetingLink) {
      return (
        <>
          <div className="event-meta-data__text-container__text-subtitle">Online-Veranstaltung</div>
          {isOverview ? <div>Online</div> : getOnlineEventLink()}
        </>
      );
    }

    if (isOverview) {
      return <div>{location.city}</div>;
    }

    return (
      <div className="event-meta-data__text-container">
        <div className="event-meta-data__text-container__text-subtitle">
          {location.description || <>Veranstaltungsort / Lokalität</>}
        </div>
        <div>
          {location.street} {location.streetNumber}
        </div>
        <div>
          {location.zip} {location.city} {location.name}
        </div>
      </div>
    );
  };
  return (
    <div className="event-meta-data">
      <div className="event-meta-data__description">
        <div className="event-meta-data__header">
          <Tags tags={tags} inverted={isOverview && isNewEntry(tags)} />
        </div>
        <div className="event-meta-data__title">{title}</div>
        <div className="event-meta-data__text">
          <Truncate
            lines={!expanded && 3}
            ellipsis={
              <div>
                {' '}
                <ExpandMoreTwoToneIcon fontSize="large" onClick={toggleLines} />{' '}
                <u>
                  <a href="#" onClick={toggleLines}>
                    Mehr anzeigen
                  </a>
                </u>
              </div>
            }
            onTruncate={handleTruncate}>
            {description}
          </Truncate>
          {!truncated && expanded && (
            <div>
              {' '}
              <ExpandLessTwoToneIcon className="less" fontSize="large" onClick={toggleLines} />{' '}
              <u>
                <a href="#" onClick={toggleLines}>
                  Weniger anzeigen
                </a>
              </u>
            </div>
          )}
        </div>
      </div>
      <div className="event-meta-data__informations">
        <div className="event-meta-data__column">
          <div className="event-meta-data__section">
            <div className="event-meta-data__icon-container">
              <CalendarIcon alt="Clock icon" className="event-meta-data__icon" />
            </div>
            <div className="event-meta-data__text-container">
              {eventDaysForDisplay.map((item) => (
                <div key={`${item.date}`}>
                  <div>
                    Veranstaltungstag{' '}
                    <span className="event-meta-data__text-container__text-strong">
                      {formatDate(item.date)}
                    </span>
                  </div>
                  {!isOverview && (
                    <div>
                      Von{' '}
                      <span className="event-meta-data__text-container__text-strong">
                        {item.startTime}
                      </span>{' '}
                      bis{' '}
                      <span className="event-meta-data__text-container__text-strong">
                        {item.endTime}{' '}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="event-meta-data__section">
            <div className="event-meta-data__icon-container">
              <img alt="Location icon" src={LocationIcon} className="event-meta-data__icon" />
            </div>
            <div className="event-meta-data__text-container">{renderLocation()}</div>
          </div>
        </div>

        <div className="event-meta-data__column">
          {registrationDeadline ? (
            <div className="event-meta-data__section">
              <div className="event-meta-data__icon-container">
                <ClockIcon alt="Tick icon" className="event-meta-data__icon" />
              </div>
              <div className="event-meta-data__text-container__horizontal">
                Anmeldeschluss:
                <span className="event-meta-data__text-container__text-strong">
                  {` ${formatDate(registrationDeadline)}`}
                </span>
              </div>
            </div>
          ) : null}
          {isRegistered ? (
            <div className="event-meta-data__section">
              <div className="event-meta-data__icon-container">
                <img alt="Tick icon" src={TickIcon} className="event-meta-data__icon" />
              </div>
              <div className="event-meta-data__text-container">
                <div>
                  {isEiamUser
                    ? 'Teilnahme angemeldet'
                    : `Teilnahme mit ${participants.length} ${
                        participants.length > 1 ? 'Personen' : 'Person'
                      }`}
                </div>
              </div>
            </div>
          ) : null}

          {attachmentUrl && isRegistered ? (
            <div className="event-meta-data__section">
              <a
                className="event-meta-data__section"
                href={getSanitzedAttachmentUrl(attachmentUrl)}
                download>
                <div className="event-meta-data__icon-container">
                  <DownloadIcon alt="Download icon" className="event-meta-data__icon__link" />
                </div>

                <div className="event-meta-data__item-link">
                  <u>Anhang zum Download</u>
                </div>
              </a>
            </div>
          ) : null}
          {isRegistrationExpired || disableMaxParticipation || isEiamUser ? null : (
            <div className="event-meta-data__section">
              <div className="event-meta-data__icon-container">
                <ParticipantsIcon alt="Participants icon" className="event-meta-data__icon" />
              </div>

              <div className="event-meta-data__text-container">
                <div>
                  {maxParticipants > eventParticipantsNumber ? (
                    <div>
                      Noch{' '}
                      {maxParticipants - eventParticipantsNumber === 1 ? (
                        <>
                          <span className="event-meta-data__text-container__text-strong">1 </span>
                          freier Platz
                        </>
                      ) : (
                        <>
                          <span className="event-meta-data__text-container__text-strong">
                            {maxParticipants - eventParticipantsNumber}{' '}
                          </span>
                          freie Plätze
                        </>
                      )}
                    </div>
                  ) : (
                    <>Die Veranstaltung ist leider ausgebucht</>
                  )}
                </div>
              </div>
            </div>
          )}
          {tags.includes(FORM_CONSTANTS.TAGS_EVENTS.KOSTENPFLICHTIG) && (
            <div className="event-meta-data__section">
              <div className="event-meta-data__icon-container">
                <EuroIcon alt="Euro icon" className="event-meta-data__icon__fill" />
              </div>
              <div className="event-meta-data__text-container__horizontal">
                Anmeldegebühr je Teilnehmer:
                <span className="event-meta-data__text-container__text-strong">
                  {chargingAmount ? chargingAmount.replace('.', ',') : '0'}€
                  {/* this should never happen but just in case  */}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EventMetaData.propTypes = {
  iamType: PropTypes.string.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    maxParticipants: PropTypes.number,
    registration: PropTypes.shape({
      eventParticipantsNumber: PropTypes.number,
      participants: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    description: PropTypes.string.isRequired,
    location: PropTypes.shape({
      description: PropTypes.string,
      street: PropTypes.string,
      streetNumber: PropTypes.string,
      zip: PropTypes.string,
      city: PropTypes.string,
      name: PropTypes.string,
    }),
    eventDays: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        startTime: PropTypes.string.isRequired,
        endTime: PropTypes.string.isRequired,
      })
    ).isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    attachmentUrl: PropTypes.string,
    registrationDeadline: PropTypes.string,
    meetingLink: PropTypes.string,
    disableMaxParticipation: PropTypes.bool,
    startDate: PropTypes.string,
    chargingAmount: PropTypes.string,
  }).isRequired,
  isOverview: PropTypes.bool,
};

EventMetaData.defaultProps = {
  isOverview: false,
};
